import React, { useEffect, useState } from 'react';
import { gsap } from 'gsap';
import Modal from './Modal';
import './Slide4.css';

const Slide4Component = () => {
  const [typedText, setTypedText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (cardType) => {
    setModalContent(getModalContent(cardType));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getModalContent = (type) => {
    const contents = {
      integration: {
        title: 'Intégration Native',
        icon: 'bi bi-cpu',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-box-seam"></i> L'IA embarquée dans votre navigateur</h3>
              <p>
                L'intégration native des modèles d'IA dans MarIAne représente une rupture technologique majeure.
                Contrairement aux solutions cloud qui nécessitent l'envoi de vos données vers des serveurs distants,
                nos modèles de langage sont directement intégrés dans votre navigateur. Cela signifie que l'intelligence
                artificielle travaille pour vous, sur votre machine, sans jamais exposer vos recherches ou vos données
                personnelles à des tiers.
              </p>
              <p>
                Cette approche présente des avantages considérables : zéro latence réseau, fonctionnement hors ligne
                pour les fonctionnalités de base, protection absolue de la vie privée, et aucune dépendance à des
                services externes qui pourraient disparaître, changer leurs conditions ou être compromis. Votre IA
                vous appartient vraiment.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800&q=80"
              alt="Processeur et intelligence artificielle"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-shield-lock"></i> Traitement local et confidentialité</h3>
              <p>
                Le traitement local est au cœur de notre philosophie de respect de la vie privée. Chaque requête,
                chaque conversation avec l'assistant IA, chaque analyse de contenu se fait entièrement sur votre
                appareil. Vos pensées, vos questions, vos recherches ne quittent jamais votre contrôle. Il n'y a
                pas de serveur qui enregistre vos activités, pas d'algorithme qui profile vos centres d'intérêt,
                pas de marketeur qui analyse vos comportements.
              </p>
              <p>
                Cette architecture technique n'est pas seulement une question de principe, c'est une garantie
                technologique. Même si nous le voulions, nous ne pourrions pas accéder à vos données de recherche
                car elles ne transitent jamais par nos serveurs. C'est une protection technique, pas juste une
                promesse dans des conditions d'utilisation que personne ne lit.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "L'intégration native des modèles IA n'est pas qu'une prouesse technique.
              C'est un choix éthique fondamental : votre intelligence artificielle personnelle ne doit jamais
              devenir un outil de surveillance."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-plug"></i> Aucune dépendance externe</h3>
              <p>
                En éliminant la dépendance aux API tierces, MarIAne vous protège contre plusieurs risques majeurs.
                Plus de pannes de service quand le fournisseur d'IA a des problèmes techniques. Plus de changements
                soudains de tarification ou de conditions d'utilisation. Plus de risque que vos données soient
                compromises lors d'une faille de sécurité chez un prestataire externe. Plus de censure arbitraire
                ou de biais imposés par des politiques d'entreprise changeantes.
              </p>
              <p>
                Vous avez l'IA que vous avez téléchargée, point. Elle fonctionne selon vos règles, sur votre machine,
                pour vos besoins. C'est la vraie indépendance numérique appliquée à l'intelligence artificielle.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80"
              alt="Autonomie et indépendance technologique"
              className="modal-image"
            />
          </>
        )
      },
      features: {
        title: 'Fonctionnalités IA',
        icon: 'bi bi-lightning-charge',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-chat-square-text"></i> Compréhension du langage naturel</h3>
              <p>
                La compréhension du langage naturel est la fondation de toute interaction intelligente avec MarIAne.
                Notre modèle ne se contente pas de reconnaître des mots-clés, il comprend véritablement le sens,
                le contexte et l'intention derrière vos requêtes. Vous pouvez poser des questions complexes,
                utiliser des formulations ambiguës, faire des références implicites, et l'IA comprendra ce que
                vous voulez vraiment savoir.
              </p>
              <p>
                Cette compréhension profonde permet des interactions beaucoup plus naturelles et efficaces.
                Plus besoin de "parler robot" en alignant des mots-clés. Demandez "Pourquoi les feuilles changent
                de couleur en automne ?" plutôt que "changement couleur feuilles automne explication". L'IA comprend
                les deux, mais la première est tellement plus naturelle pour vous.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80"
              alt="Traitement du langage naturel"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-file-earmark-text"></i> Génération de résumés contextuels</h3>
              <p>
                Les résumés générés par MarIAne ne sont pas de simples extraits aléatoires. L'IA analyse le contenu
                complet de chaque page, identifie les informations les plus pertinentes par rapport à votre requête
                spécifique, et génère un résumé qui répond directement à votre besoin d'information. Si vous cherchez
                une recette de gâteau, le résumé mettra en avant les ingrédients et le temps de préparation. Si vous
                cherchez une critique de film, il mettra en avant l'avis et les points clés.
              </p>
              <p>
                Cette contextualisation intelligente transforme votre expérience de recherche. Au lieu de devoir
                lire plusieurs pages pour trouver l'information précise que vous cherchez, le résumé vous donne
                immédiatement l'essentiel. Vous pouvez ensuite choisir d'approfondir si nécessaire, mais vous avez
                déjà la réponse à votre question initiale.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Les fonctionnalités IA de MarIAne ne sont pas des gadgets marketing.
              Ce sont des outils pratiques qui vous font gagner du temps et améliorent réellement votre
              compréhension de l'information."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-lightbulb"></i> Suggestions intelligentes</h3>
              <p>
                Le système de suggestions de MarIAne va bien au-delà des simples suggestions de recherche basées
                sur la popularité. L'IA analyse le contexte de votre recherche, votre historique de session, et
                les informations déjà découvertes pour vous proposer des pistes d'exploration pertinentes et personnalisées.
                Ces suggestions vous aident à découvrir des angles que vous n'auriez pas envisagés et à approfondir
                votre compréhension du sujet.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80"
              alt="Suggestions et découverte intelligente"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-chat-left-dots"></i> Réponses conversationnelles</h3>
              <p>
                L'assistant conversationnel de MarIAne maintient un dialogue fluide et cohérent. Il se souvient
                de ce que vous avez demandé précédemment, comprend les questions de suivi, et adapte ses réponses
                en fonction du contexte de la conversation. C'est une vraie discussion avec un assistant intelligent,
                pas une série de requêtes indépendantes. Cette continuité rend l'exploration de sujets complexes
                beaucoup plus naturelle et efficace.
              </p>
            </div>
          </>
        )
      },
      performance: {
        title: 'Performance',
        icon: 'bi bi-speedometer2',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-lightning"></i> Réponses instantanées</h3>
              <p>
                La vitesse est cruciale pour une expérience de recherche fluide. Avec le traitement local de MarIAne,
                vos requêtes sont analysées instantanément sans avoir à attendre qu'elles soient envoyées à un serveur
                distant, traitées, puis renvoyées. L'IA sur votre machine réagit en temps réel, rendant l'interaction
                aussi rapide et naturelle qu'une conversation humaine.
              </p>
              <p>
                Cette réactivité transforme complètement l'expérience utilisateur. Plus de temps mort frustrant
                entre votre question et la réponse. Plus d'indicateurs de chargement qui tournent. L'information
                arrive instantanément, vous permettant de maintenir votre flux de pensée et d'explorer les sujets
                de manière beaucoup plus fluide et naturelle.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1534972195531-d756b9bfa9f2?w=800&q=80"
              alt="Vitesse et performance"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-shield-check"></i> Traitement local pour la confidentialité</h3>
              <p>
                Le traitement local n'est pas qu'une question de vitesse, c'est aussi la garantie ultime de
                confidentialité. Chaque analyse, chaque génération de résumé, chaque réponse conversationnelle
                se fait entièrement sur votre appareil. Vos données ne sont jamais exposées, jamais transmises,
                jamais enregistrées sur des serveurs distants. C'est la protection de la vie privée par design,
                pas par politique.
              </p>
              <p>
                Cette architecture garantit que même en cas de faille de sécurité hypothétique chez MarIAne,
                vos données de recherche resteraient en sécurité car elles n'ont jamais quitté votre machine.
                C'est une protection technique fondamentale, pas juste une promesse marketing. La meilleure
                façon de protéger vos données est de ne jamais les collecter, et c'est exactement ce que nous faisons.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "La vraie performance n'est pas seulement la vitesse d'exécution.
              C'est aussi la capacité à offrir une expérience exceptionnelle tout en respectant
              absolument la vie privée de l'utilisateur."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-arrow-clockwise"></i> Optimisation continue des résultats</h3>
              <p>
                Les modèles IA de MarIAne apprennent et s'améliorent au fil du temps, mais toujours de manière
                locale et privée. Le système observe quels résultats vous trouvez utiles, quelles formulations
                fonctionnent le mieux, et optimise progressivement ses réponses pour mieux correspondre à vos
                besoins spécifiques. Cette personnalisation se fait entièrement sur votre appareil, sans jamais
                partager vos préférences avec des serveurs externes.
              </p>
              <p>
                L'optimisation continue signifie que votre expérience avec MarIAne s'améliore constamment.
                Plus vous l'utilisez, plus l'IA comprend votre style de recherche, vos centres d'intérêt,
                et vos préférences de présentation. C'est une personnalisation intelligente sans le côté
                effrayant du profilage publicitaire.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
              alt="Amélioration continue et optimisation"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-cpu"></i> Efficacité des ressources</h3>
              <p>
                Malgré la puissance de nos modèles IA, MarIAne est conçu pour être efficace en termes de
                ressources. Les modèles sont optimisés pour fonctionner même sur des machines modestes,
                utilisant des techniques avancées de compression et de quantification. Vous n'avez pas besoin
                d'un ordinateur ultra-puissant pour bénéficier d'une IA performante. L'intelligence accessible
                à tous, c'est aussi ça, l'éthique de MarIAne.
              </p>
            </div>
          </>
        )
      }
    };

    return contents[type];
  };

  useEffect(() => {
    const tl = gsap.timeline();

    tl.set('.slide4-header', { opacity: 1 });

    tl.add(() => {
      const text = "Quels modèles IA sont embarqués ?";
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index <= text.length) {
          setTypedText(text.slice(0, index));
          index++;
        } else {
          clearInterval(typeInterval);
        }
      }, 50);
    }, '+=0.8');

    tl.to('.slide4-search-button', {
      scale: 1.1,
      boxShadow: '0 0 40px rgba(0, 212, 255, 0.8)',
      duration: 0.3,
      repeat: 1,
      yoyo: true
    }, '+=2');

    tl.fromTo('.slide4-main-title',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
    , '+=0.3');

    tl.fromTo('.slide4-separator',
      { opacity: 0, scaleX: 0 },
      { opacity: 1, scaleX: 1, duration: 0.8, ease: 'power2.inOut' }
    , '-=0.4');

    tl.fromTo('.ai-feature-card',
      { opacity: 0, y: 50, scale: 0.95 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.12, ease: 'power2.out' }
    , '+=0.2');

    return () => tl.kill();
  }, []);

  return (
    <div className="slide-content slide-4-transition">
      <header className="slide4-header">
        <div className="slide4-header-content">
          <div className="slide4-logo-small">
            MAR<span className="neon-text">IA</span>NE
          </div>
          <form className="slide4-search-form">
            <div className="slide4-search-box">
              <input
                type="text"
                className="slide4-search-input"
                placeholder="Rechercher..."
                value={typedText}
                readOnly
              />
              <button type="button" className="slide4-search-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </header>

      <div className="slide4-content">
        <div className="slide4-main-title">
          <h1 className="slide4-h1">Modèles IA Embarqués</h1>
          <p className="slide4-sub">L'Intelligence au Cœur du Moteur</p>
        </div>

        <div className="slide4-separator"></div>

        <div className="ai-features-grid">
          <div className="ai-feature-card" onClick={() => openModal('integration')} style={{ cursor: 'pointer' }}>
            <div className="feature-badge">01</div>
            <div className="feature-icon">
              <i className="bi bi-cpu"></i>
            </div>
            <h3>Intégration Native</h3>
            <ul>
              <li>Modèles de langage directement intégrés</li>
              <li>Traitement sémantique en temps réel</li>
              <li>Pas de dépendance à des services externes</li>
            </ul>
          </div>

          <div className="ai-feature-card" onClick={() => openModal('features')} style={{ cursor: 'pointer' }}>
            <div className="feature-badge">02</div>
            <div className="feature-icon">
              <i className="bi bi-lightning-charge"></i>
            </div>
            <h3>Fonctionnalités IA</h3>
            <ul>
              <li>Compréhension du langage naturel</li>
              <li>Génération de résumés contextuels</li>
              <li>Suggestions intelligentes</li>
              <li>Réponses conversationnelles</li>
            </ul>
          </div>

          <div className="ai-feature-card" onClick={() => openModal('performance')} style={{ cursor: 'pointer' }}>
            <div className="feature-badge">03</div>
            <div className="feature-icon">
              <i className="bi bi-speedometer2"></i>
            </div>
            <h3>Performance</h3>
            <ul>
              <li>Réponses instantanées</li>
              <li>Traitement local pour la confidentialité</li>
              <li>Optimisation continue des résultats</li>
            </ul>
          </div>
        </div>
      </div>

      {modalContent && (
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={modalContent.title}
          icon={modalContent.icon}
          content={modalContent.content}
        />
      )}
    </div>
  );
};

export default Slide4Component;
